import type {CSSProperties, KeyboardEvent, ReactNode} from "react";
import styles from "./PromoBanner.module.css";

type PromoBannerProps = {
  ariaLabel: string;
  title: string;
  description: string;
  tagText?: string;
  actionText?: string;
  actionContent?: ReactNode;
  onActionClick?: () => void;
  bottomLabel?: ReactNode;
  bottomValue?: ReactNode;
  badgeMain: string;
  badgeSub?: string;
  badge?: ReactNode;
  onClick?: () => void;
  themeStyle: CSSProperties;
};

const PromoBanner = ({
  ariaLabel,
  title,
  description,
  tagText,
  actionText,
  actionContent,
  onActionClick,
  bottomLabel,
  bottomValue,
  badgeMain,
  badgeSub,
  badge,
  onClick,
  themeStyle,
}: PromoBannerProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && onClick) {
      onClick();
    }
  };

  return (
    <section
      className={styles.promoBanner}
      role="banner"
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      style={themeStyle}
    >
      <div
        className={styles.glow}
        aria-hidden="true"
      />
      <div
        className={styles.glowSecondary}
        aria-hidden="true"
      />

      <div className={styles.header}>
        {badge ? (
          <div aria-hidden="true">{badge}</div>
        ) : (
          <div
            className={styles.iconBadge}
            aria-hidden="true"
          >
            <span className={styles.iconMain}>{badgeMain}</span>
            {badgeSub && <span className={styles.iconSub}>{badgeSub}</span>}
          </div>
        )}
        <div className={styles.headerText}>
          <h3 className={styles.title}>{title}</h3>
          {(actionText || actionContent) && onActionClick ? (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={e => {
                e.stopPropagation();
                onActionClick();
              }}
            >
              {actionContent ?? actionText}
            </button>
          ) : (
            tagText && (
              <span className={styles.tag}>
                <span className={styles.tagDot} />
                {tagText}
              </span>
            )
          )}
        </div>
      </div>

      <p className={styles.desc}>{description}</p>

      {bottomValue && (
        <div className={styles.chip}>
          {bottomLabel && <span className={styles.chipLabel}>{bottomLabel}</span>}
          <span className={styles.chipValue}>{bottomValue}</span>
        </div>
      )}
    </section>
  );
};

export default PromoBanner;
